import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Response } from '@ui/src';
import { pspApi } from '../services';
import {
  BankAndPspItemStatus,
  PspAccountItem,
  PspAccountLogs,
  Balance,
  Document,
  PspIntegrationAccountStatusEnum,
} from '../../types';

export interface BankAndPspAccountPageState {
  accountId?: string;
  generalInfo?: PspAccountItem;
  documents?: Document[];
  changeLog?: PspAccountLogs[];
  internalAccounts?: PspAccountItem[];
  statusChangeInProgress?: boolean;
  status?: BankAndPspItemStatus;
  balances?: Balance[];
}

const initialState: BankAndPspAccountPageState = {};

export const bankAndPspAccountPageSlice = createSlice({
  name: 'bankAndPspAccountPage',
  initialState,
  reducers: {
    setBalances: (state, action: PayloadAction<Balance[]>) => {
      state.balances = action.payload;
    },
    clearBankAndPspAccountPageState: (state) => {
      state.generalInfo = undefined;
      state.changeLog = undefined;
      state.internalAccounts = undefined;
      state.accountId = undefined;
      state.status = undefined;
      state.statusChangeInProgress = undefined;
      state.balances = undefined;
      state.documents = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      pspApi.endpoints.getPspAccount.matchFulfilled,
      (state, action: PayloadAction<{ data: PspAccountItem }>) => {
        state.generalInfo = action.payload.data;
      },
    );
    builder.addMatcher(
      pspApi.endpoints.bindPSPExternalAccount.matchFulfilled,
      (state) => {
        state.generalInfo = {
          ...state.generalInfo,
          pspIntegrationAccountStatus:
            PspIntegrationAccountStatusEnum.IN_PROGRESS,
        } as PspAccountItem;
      },
    );
    builder.addMatcher(
      pspApi.endpoints.generateNewPSPExternalAccount.matchFulfilled,
      (state) => {
        state.generalInfo = {
          ...state.generalInfo,
          pspIntegrationAccountStatus:
            PspIntegrationAccountStatusEnum.IN_PROGRESS,
        } as PspAccountItem;
      },
    );
    builder.addMatcher(
      pspApi.endpoints.getPspAccountsLogs.matchFulfilled,
      (state, action: PayloadAction<Response<PspAccountLogs>>) => {
        state.changeLog = action.payload.data.edges.map((item) => item.node);
      },
    );
    builder.addMatcher(
      pspApi.endpoints.getPspInternalAccounts.matchFulfilled,
      (state, action: PayloadAction<Response<PspAccountItem>>) => {
        state.internalAccounts = action.payload.data.edges.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      pspApi.endpoints.getPspAccountDocuments.matchFulfilled,
      (state, action: PayloadAction<Response<Document>>) => {
        state.documents = action.payload.data.edges.map((item) => item.node);
      },
    );
    builder.addMatcher(
      pspApi.endpoints.getPspAccountDocuments.matchFulfilled,
      (state, action: PayloadAction<Response<Document>>) => {
        state.documents = action.payload.data.edges.map((item) => item.node);
      },
    );
    builder.addMatcher(
      pspApi.endpoints.updatePspAccount.matchFulfilled,
      (state, action: PayloadAction<{ data: PspAccountItem }>) => {
        state.generalInfo = action.payload.data;
      },
    );
    builder.addMatcher(
      pspApi.endpoints.enablePspAccount.matchFulfilled,
      (state, action: PayloadAction<{ data: PspAccountItem }>) => {
        state.generalInfo = {
          ...state.generalInfo,
          active: action.payload.data.active,
        } as PspAccountItem;
      },
    );
    builder.addMatcher(
      pspApi.endpoints.addAccountDocuments.matchFulfilled,
      (state, action: PayloadAction<{ data: Document }>) => {
        const nextDocuments = ([] as Document[]).concat(
          state.documents as Document[],
        );

        nextDocuments.push(action.payload.data);

        state.documents = nextDocuments;
      },
    );
    builder.addMatcher(
      pspApi.endpoints.deletePspAccountDocument.matchFulfilled,
      (state, action: PayloadAction<{ data: { id: string } }>) => {
        const nextDocuments = state?.documents?.filter(
          (item) => item.id !== action.payload.data.id,
        );

        state.documents = nextDocuments;
      },
    );
  },
});

export const { setBalances, clearBankAndPspAccountPageState } =
  bankAndPspAccountPageSlice.actions;

export default bankAndPspAccountPageSlice.reducer;
